import {
  Box,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "src/assets/icon/chevron/left";
import ChevronRightIcon from "src/assets/icon/chevron/right";
import useLocales from "src/hooks/useLocales";

type PaginationProps = {
  page: number;
  record: number;
  count: number;
  onChangePage: (event: any, newPage: number) => void;
  // hasNextPage is required if hidePageNo is true
  hasNextPage?: boolean;
  hidePageNo?: boolean;
  dataLength?: number;
  loading?: boolean;
};

const Pagination = ({
  page,
  record,
  count,
  onChangePage,
  hasNextPage,
  hidePageNo,
  dataLength,
  loading,
}: PaginationProps) => {
  const theme = useTheme();
  const { palette } = theme;
  const iconColor = palette?.grey["600"];
  const { translate } = useLocales();

  const showNext = hidePageNo ? hasNextPage : page * record + record < count;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        "& svg": { m: 1.5 },
      }}
    >
      <Typography variant="subtitle2">
        {loading ? (
          <Skeleton variant="text" width={60} height={24} />
        ) : (
          !isNaN(page) &&
          !isNaN(record) &&
          (() => {
            const start = page * record + 1;
            const end = hidePageNo
              ? start + (dataLength ?? 0) - 1
              : Math.min(count, page * record + record);

            if ((hidePageNo && (dataLength ?? 0) === 0) || end < start)
              return null;

            return (
              <>
                {start}–{end}
                {!hidePageNo && count > 0 && ` ${translate("of_")} ${count}`}
              </>
            );
          })()
        )}
      </Typography>

      <Tooltip title={translate("previous_page")}>
        <span>
          <IconButton
            size="small"
            disabled={loading || page === 0}
            sx={{
              marginRight: 1,
              marginLeft: 1,
              height: "30px",
              width: "30px",
              color: palette?.grey["400"],
              bgcolor: palette?.grey["300"],
              cursor: "pointer",
              "&:hover": { color: "black" },
              "&:disabled": {
                color: palette?.grey["400"],
                bgcolor: palette?.grey["300"],
              },
            }}
            onClick={(e) => onChangePage(e, page - 1)}
          >
            <ChevronLeftIcon
              height={18}
              width={18}
              color={
                loading || page === 0
                  ? palette?.action.disabled
                  : theme.palette.primary.light
              }
            />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={translate("next_page")}>
        <span>
          <IconButton
            size="small"
            disabled={loading || !showNext}
            sx={{
              height: "30px",
              width: "30px",
              color: iconColor,
              bgcolor: palette?.grey["300"],
              cursor: "pointer",
              "&:hover": { color: "black" },
              "&:disabled": {
                color: palette?.grey["400"],
                bgcolor: palette?.grey["300"],
              },
            }}
            onClick={(e) => onChangePage(e, page + 1)}
          >
            <ChevronRightIcon
              height={18}
              width={18}
              color={
                loading || !showNext
                  ? palette?.action.disabled
                  : theme.palette.primary.light
              }
            />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default Pagination;
